import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';
import {
  Scrim,
  Modal,
  Btn,
  Field,
  Input,
  Select,
  Textarea,
  FormGrid,
} from '../ui/primitives';
import { createCampaign, updateCampaign } from '../services/campaignAdminApi';

const TYPE_OPTIONS = [
  { value: 'video', label: 'Video only' },
  { value: 'assessment', label: 'Assessment only' },
  { value: 'both', label: 'Video, then assessment' },
];

/** Whether the selected type shows a demo video (drives the demo-video fields). */
function typeHasVideo(type) {
  return type === 'video' || type === 'both';
}

const EMPTY = {
  name: '',
  description: '',
  type: 'assessment',
  showDemoVideo: 0,
  demoVideoUrl: '',
  demoVideoTitle: '',
  demoVideoSubtitle: '',
  isLeadFormEnabled: 0,
  active: 1,
  startDate: '',
  endDate: '',
};

/**
 * Create/edit dialog for a campaign. When `campaign` is provided the dialog edits it
 * (PUT); otherwise it creates a new one (POST). Calls `onSaved()` after a successful write.
 */
export default function CampaignFormDialog({ open, campaign, onClose, onSaved }) {
  const isEdit = Boolean(campaign?.campaignId);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    if (isEdit) {
      setForm({
        name: campaign.name || '',
        description: campaign.description || '',
        type: campaign.type || 'assessment',
        showDemoVideo: campaign.showDemoVideo ? 1 : 0,
        demoVideoUrl: campaign.demoVideoUrl || '',
        demoVideoTitle: campaign.demoVideoTitle || '',
        demoVideoSubtitle: campaign.demoVideoSubtitle || '',
        isLeadFormEnabled: campaign.isLeadFormEnabled ? 1 : 0,
        active: campaign.active ? 1 : 0,
        startDate: campaign.startDate || '',
        endDate: campaign.endDate || '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, isEdit, campaign]);

  const showVideoFields = useMemo(
    () => typeHasVideo(form.type) || form.showDemoVideo === 1,
    [form.type, form.showDemoVideo],
  );

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Campaign name is required.');
      return;
    }
    setSaving(true);
    setError('');
    const payload = {
      name: form.name.trim(),
      description: form.description,
      type: form.type,
      showDemoVideo: showVideoFields ? 1 : 0,
      demoVideoUrl: form.demoVideoUrl,
      demoVideoTitle: form.demoVideoTitle,
      demoVideoSubtitle: form.demoVideoSubtitle,
      isLeadFormEnabled: form.isLeadFormEnabled ? 1 : 0,
      active: form.active ? 1 : 0,
      startDate: form.startDate,
      endDate: form.endDate,
    };
    try {
      if (isEdit) {
        await updateCampaign(campaign.campaignId, payload);
      } else {
        await createCampaign(payload);
      }
      onSaved?.();
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Scrim onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <Modal>
        <div className="modal__head">
          <div>
            <h3>{isEdit ? 'Edit campaign' : 'New campaign'}</h3>
            <p>
              {isEdit
                ? 'Update the campaign shown to attached organizations.'
                : 'Create a campaign, then attach organizations to it.'}
            </p>
          </div>
          <button className="x-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal__body">
          {error && (
            <div style={{ color: 'crimson', fontSize: 13, marginBottom: 14 }}>{error}</div>
          )}

          <Field>
            <label>Name *</label>
            <Input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Gap Is Real — Fall 2026"
            />
          </Field>

          <Field>
            <label>Description</label>
            <Textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Internal note about this campaign."
            />
          </Field>

          <FormGrid>
            <Field>
              <label>Type</label>
              <Select value={form.type} onChange={(e) => set('type', e.target.value)}>
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </Field>
            <Field>
              <label>Status</label>
              <Select
                value={String(form.active)}
                onChange={(e) => set('active', Number(e.target.value))}
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </Select>
            </Field>
          </FormGrid>

          {showVideoFields && (
            <>
              <Field>
                <label>Demo video URL</label>
                <Input
                  value={form.demoVideoUrl}
                  onChange={(e) => set('demoVideoUrl', e.target.value)}
                  placeholder="https://…"
                />
              </Field>
              <FormGrid>
                <Field>
                  <label>Video title</label>
                  <Input
                    value={form.demoVideoTitle}
                    onChange={(e) => set('demoVideoTitle', e.target.value)}
                  />
                </Field>
                <Field>
                  <label>Video subtitle</label>
                  <Input
                    value={form.demoVideoSubtitle}
                    onChange={(e) => set('demoVideoSubtitle', e.target.value)}
                  />
                </Field>
              </FormGrid>
            </>
          )}

          <FormGrid>
            <Field>
              <label>Start date</label>
              <Input
                type="date"
                value={form.startDate}
                onChange={(e) => set('startDate', e.target.value)}
              />
            </Field>
            <Field>
              <label>End date</label>
              <Input
                type="date"
                value={form.endDate}
                onChange={(e) => set('endDate', e.target.value)}
              />
            </Field>
          </FormGrid>

          <Field>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.isLeadFormEnabled === 1}
                onChange={(e) => set('isLeadFormEnabled', e.target.checked ? 1 : 0)}
              />
              Show lead-capture form
            </label>
          </Field>
        </div>

        <div className="modal__foot">
          <Btn $variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={14} color="inherit" /> : (isEdit ? 'Save changes' : 'Create campaign')}
          </Btn>
        </div>
      </Modal>
    </Scrim>
  );
}
